import type { SerializedLexicalNode } from "lexical";
import { getSurveyIdFromUrl } from "../../message/editor/components/utils/survey-url";
import type { SerialisedEditorDesign } from "../../message/editor/types/serialised-editor-design";
import type { UnlayerDesign } from "../../unlayer-email-content/types/unlayer-design";
import type { UUID } from "../../../utils/uuid";

export type DynamicLinkSource =
  | { type: "lexical"; design: SerialisedEditorDesign }
  | { type: "unlayer"; design: UnlayerDesign }
  | { type: "email_html"; html: string };

export function extractDynamicOnlyLinks(source: DynamicLinkSource | null): Set<string> {
  if (!source) return new Set();
  if (source.type === "lexical") return extractLexical(source.design);
  if (source.type === "email_html") return extractEmailHtml(source.html);
  return extractUnlayer(source.design);
}

export function extractDynamicOnlySurveyIds(source: DynamicLinkSource | null): Set<string> {
  if (!source) return new Set();
  if (source.type === "lexical") return extractLexicalSurveyIds(source.design);
  if (source.type === "email_html") return extractEmailHtmlSurveyIds(source.html);
  return extractUnlayerSurveyIds(source.design);
}

// ── Lexical ────────────────────────────────────────────────────────────────

type NodeWithChildren = SerializedLexicalNode & { children?: SerializedLexicalNode[] };
type LinkNode = SerializedLexicalNode & { url: string };

function isLinkNode(node: SerializedLexicalNode): node is LinkNode {
  return (
    (node.type === "link" || node.type === "autolink" || node.type === "styleablebutton") &&
    typeof (node as LinkNode).url === "string"
  );
}

function collectLexicalUrls(
  node: NodeWithChildren,
  insideDynamic: boolean,
  dynamicUrls: Set<string>,
  allUrls: Set<string>,
): void {
  const nowInsideDynamic = insideDynamic || node.type === "dynamic";

  if (isLinkNode(node)) {
    const url = node.url;
    if (nowInsideDynamic) {
      dynamicUrls.add(url);
    } else {
      allUrls.add(url);
    }
  }

  const children = node.children;
  if (children) {
    for (const child of children) {
      collectLexicalUrls(child as NodeWithChildren, nowInsideDynamic, dynamicUrls, allUrls);
    }
  }
}

function extractLexical(design: SerialisedEditorDesign): Set<string> {
  if (design.type === "markdown") return new Set();

  const dynamicUrls = new Set<string>();
  const allUrls = new Set<string>();

  const root = (design as { root?: NodeWithChildren }).root;
  if (!root) return new Set();

  collectLexicalUrls(root, false, dynamicUrls, allUrls);

  return new Set([...dynamicUrls].filter((url) => !allUrls.has(url)));
}

// ── Unlayer ────────────────────────────────────────────────────────────────

const EMAIL_HREF_REG_EXP =
  /(?<=<(a|mj-button|v:\w+)(?:[^>]*?)\s+href=")(https?:\/\/(?:(?!{{#jwt}})[^\s">]|{{#jwt}}[\s\S]*?{{\/jwt}})+)/g;

function extractUrlsFromHtml(html: string): string[] {
  const matches = html.match(EMAIL_HREF_REG_EXP);
  return matches ?? [];
}

type ContentBlock = {
  type?: string;
  values?: {
    text?: string;
    href?: { attrs?: { href?: string }; values?: { href?: string } };
    [key: string]: unknown;
  };
};

type ColumnLike = { contents?: ContentBlock[]; values?: { displayCondition?: unknown } };
type RowLike = { columns?: ColumnLike[]; values?: { displayCondition?: unknown } };

function hasDisplayCondition(values: unknown): boolean {
  if (!values || typeof values !== "object") return false;
  const dc = (values as { displayCondition?: unknown }).displayCondition;
  if (!dc || typeof dc !== "object") return false;
  const c = dc as Record<string, unknown>;
  return Boolean(c["type"] || c["label"] || c["description"] || c["before"] || c["after"]);
}

function extractUrlsFromContents(contents: ContentBlock[]): string[] {
  const urls: string[] = [];
  for (const block of contents) {
    if (block.values?.text) {
      urls.push(...extractUrlsFromHtml(block.values.text));
    }
    // Real Unlayer designs store the actual URL in href.values.href; href.attrs.href is always "{{href}}"
    const href = block.values?.href?.values?.href ?? block.values?.href?.attrs?.href;
    if (href && href.startsWith("http")) {
      urls.push(href);
    }
  }
  return urls;
}

function extractUnlayer(design: UnlayerDesign): Set<string> {
  const rows: RowLike[] = [
    ...design.body.rows,
    ...(design.body.headers ?? []),
    ...(design.body.footers ?? []),
  ] as unknown as RowLike[];

  const dynamicUrls = new Set<string>();
  const nonDynamicUrls = new Set<string>();

  for (const row of rows) {
    const rowIsDynamic = hasDisplayCondition(row.values);
    for (const column of row.columns ?? []) {
      const colIsDynamic = rowIsDynamic || hasDisplayCondition(column.values);
      const urls = extractUrlsFromContents(column.contents ?? []);
      if (colIsDynamic) {
        urls.forEach((u) => dynamicUrls.add(u));
      } else {
        urls.forEach((u) => nonDynamicUrls.add(u));
      }
    }
  }

  const result = new Set([...dynamicUrls].filter((url) => !nonDynamicUrls.has(url)));
  return result;
}

// ── Survey ID extraction ───────────────────────────────────────────────────

function isSurveyLinkNode(node: SerializedLexicalNode): node is LinkNode {
  return node.type === "surveylink" && typeof (node as LinkNode).url === "string";
}

function collectLexicalSurveyIds(
  node: NodeWithChildren,
  insideDynamic: boolean,
  dynamicIds: Set<string>,
  allIds: Set<string>,
): void {
  const nowInsideDynamic = insideDynamic || node.type === "dynamic";

  if (isLinkNode(node) || isSurveyLinkNode(node)) {
    const surveyId = getSurveyIdFromUrl(node.url);
    if (surveyId) {
      if (nowInsideDynamic) {
        dynamicIds.add(surveyId);
      } else {
        allIds.add(surveyId);
      }
    }
  }

  const children = node.children;
  if (children) {
    for (const child of children) {
      collectLexicalSurveyIds(child as NodeWithChildren, nowInsideDynamic, dynamicIds, allIds);
    }
  }
}

function extractLexicalSurveyIds(design: SerialisedEditorDesign): Set<string> {
  if (design.type === "markdown") return new Set();

  const dynamicIds = new Set<string>();
  const allIds = new Set<string>();

  const root = (design as { root?: NodeWithChildren }).root;
  if (!root) return new Set();

  collectLexicalSurveyIds(root, false, dynamicIds, allIds);

  return new Set([...dynamicIds].filter((id) => !allIds.has(id)));
}

// ── Email HTML (rendered Unlayer body with Handlebars conditionals) ────────

const HANDLEBARS_IF_BLOCK_REG_EXP = /\{\{#if\s+\w+\}\}([\s\S]*?)\{\{\/if\}\}/g;

function extractEmailHtml(html: string): Set<string> {
  const dynamicUrls = new Set<string>();
  const nonDynamicHtml = html.replace(HANDLEBARS_IF_BLOCK_REG_EXP, (_, blockContent: string) => {
    extractUrlsFromHtml(blockContent).forEach((url) => dynamicUrls.add(url));
    return "";
  });
  const nonDynamicUrls = new Set(extractUrlsFromHtml(nonDynamicHtml));
  return new Set([...dynamicUrls].filter((url) => !nonDynamicUrls.has(url)));
}

function extractEmailHtmlSurveyIds(html: string): Set<string> {
  const dynamicIds = new Set<string>();
  const nonDynamicHtml = html.replace(HANDLEBARS_IF_BLOCK_REG_EXP, (_, blockContent: string) => {
    extractUrlsFromHtml(blockContent)
      .map((url) => getSurveyIdFromUrl(url))
      .filter((id): id is UUID => id !== null)
      .forEach((id) => dynamicIds.add(id));
    return "";
  });
  const nonDynamicIds = new Set<string>(
    extractUrlsFromHtml(nonDynamicHtml)
      .map((url) => getSurveyIdFromUrl(url))
      .filter((id): id is UUID => id !== null),
  );
  return new Set([...dynamicIds].filter((id) => !nonDynamicIds.has(id)));
}

function extractUnlayerSurveyIds(design: UnlayerDesign): Set<string> {
  const rows: RowLike[] = [
    ...design.body.rows,
    ...(design.body.headers ?? []),
    ...(design.body.footers ?? []),
  ] as unknown as RowLike[];

  const dynamicIds = new Set<string>();
  const nonDynamicIds = new Set<string>();

  for (const row of rows) {
    const rowIsDynamic = hasDisplayCondition(row.values);
    for (const column of row.columns ?? []) {
      const colIsDynamic = rowIsDynamic || hasDisplayCondition(column.values);
      const ids = extractUrlsFromContents(column.contents ?? [])
        .map((url) => getSurveyIdFromUrl(url))
        .filter((id): id is UUID => id !== null);
      if (colIsDynamic) {
        ids.forEach((id) => dynamicIds.add(id));
      } else {
        ids.forEach((id) => nonDynamicIds.add(id));
      }
    }
  }

  return new Set([...dynamicIds].filter((id) => !nonDynamicIds.has(id)));
}
